// First-person controller in ship-local voxel space. The player is an AABB
// (0.6 m square, 1.8 m tall) with eye at 1.62 m; gravity, jumping, 1-voxel
// step-up assist on stair-less deck lips, and ladder climbing on LADDER
// cells. All movement and collision happen in ship-local coordinates so the
// player rides the ship for free.

import * as THREE from "three";

const EYE = 1.62, HEIGHT = 1.8, HALF = 0.3;
const WALK = 4.2, RUN = 7.0, ACCEL = 40, AIR_ACCEL = 8;
const GRAV = 22, JUMP = 7.6, CLIMB = 2.4;

export class Player {
  constructor(grid, spawn) {
    this.grid = grid;
    this.pos = spawn.clone().add(new THREE.Vector3(0.5, 0.01, 0)); // feet position
    this.vel = new THREE.Vector3();
    this.yaw = 0;           // 0 = facing +X (bow), + rotates toward +Z (stbd)
    this.pitch = 0;
    this.onGround = false;
    this.onLadder = false;
    this.frozen = false;    // true while manning a station
    this.keys = new Set();
    this.zoom = 1;          // FOV divider (binoculars)
    // Analog stick (touch.js): x strafes, y walks, run engages at full
    // deflection — but only once already moving (see update), so easing up
    // to a deck hatch never turns into a sprint onto the ladder.
    this.pad = { x: 0, y: 0, run: false };
  }

  eyePos() { return new THREE.Vector3(this.pos.x, this.pos.y + EYE, this.pos.z); }
  forward() {
    return new THREE.Vector3(
      Math.cos(this.yaw) * Math.cos(this.pitch),
      Math.sin(this.pitch),
      Math.sin(this.yaw) * Math.cos(this.pitch));
  }

  look(dx, dy) {
    if (this.frozen) return;
    const s = 0.0023 / Math.sqrt(this.zoom);
    this.yaw += dx * s;
    this.pitch = Math.max(-1.45, Math.min(1.45, this.pitch - dy * s));
  }

  update(dt) {
    if (this.frozen) return;
    dt = Math.min(dt, 0.05);
    const k = this.keys;
    const fwd = Math.max(-1, Math.min(1, (k.has("KeyW") ? 1 : 0) - (k.has("KeyS") ? 1 : 0) + this.pad.y));
    const str = Math.max(-1, Math.min(1, (k.has("KeyD") ? 1 : 0) - (k.has("KeyA") ? 1 : 0) + this.pad.x));
    // The stick's run only kicks in above walking pace: the SHIFT-while-slow
    // ladder grab must stay a deliberate act, not a side effect of pushing
    // the stick hard from a standstill next to a hatch.
    const padRun = this.pad.run && Math.hypot(this.vel.x, this.vel.z) > 3.2;
    const shift = k.has("ShiftLeft") || k.has("ShiftRight") || padRun;

    // Ladder check: does our AABB overlap a climbable cell — and do we mean
    // to be on it? Standing on deck you only grab with intent (SPACE, SHIFT
    // to go down, or W while looking up the ladder); falling past one always
    // catches. Keeps the bridge walkable even though a trunk crosses it.
    const slow = Math.hypot(this.vel.x, this.vel.z) < 3; // not mid-sprint
    const intent = k.has("Space") || (shift && slow) || (fwd > 0 && this.pitch > 0.35) || !this.onGround;
    this.onLadder = this.touchingLadder(0) && intent;
    // reaching for a nearby trunk to climb down: SHIFT near the hatch grabs it
    if (!this.onLadder && shift && slow && this.touchingLadder(0.35)) {
      this.onLadder = true;
    }

    if (this.onLadder) {
      // W climbs the way you're looking; S descends.
      let vy = 0;
      if (fwd) vy = (this.pitch < -0.25 ? -fwd : fwd) * CLIMB;
      if (k.has("Space")) vy = CLIMB;
      if (shift) vy = -CLIMB;
      this.vel.y = vy;
      // gentle lateral drift so you can step off at the top
      const dir = new THREE.Vector3(Math.cos(this.yaw), 0, Math.sin(this.yaw));
      const side = new THREE.Vector3(-Math.sin(this.yaw), 0, Math.cos(this.yaw));
      const wish = dir.multiplyScalar(fwd).add(side.multiplyScalar(str));
      this.vel.x = wish.x * 1.6; this.vel.z = wish.z * 1.6;
      // magnetize toward the column so 1-meter deck hatches thread cleanly;
      // soft when the player is steering so stepping off still works
      const c = this.nearestLadderCell(0.5);
      if (c) {
        const pull = (fwd || str) ? 0.7 : 2.4;
        this.vel.x += Math.max(-pull, Math.min(pull, (c[0] + 0.5 - this.pos.x) * 6));
        this.vel.z += Math.max(-pull, Math.min(pull, (c[2] + 0.5 - this.pos.z) * 6));
      }
    } else {
      const speed = shift ? RUN : WALK;
      const dir = new THREE.Vector3(Math.cos(this.yaw), 0, Math.sin(this.yaw));
      const side = new THREE.Vector3(-Math.sin(this.yaw), 0, Math.cos(this.yaw));
      const wish = dir.multiplyScalar(fwd).add(side.multiplyScalar(str));
      // keys hit the rim (length 1+); a half-pushed stick keeps its fraction
      // of walking pace — that's the whole point of an analog thumb
      if (wish.lengthSq() > 1) wish.normalize();
      wish.multiplyScalar(speed);
      const accel = this.onGround ? ACCEL : AIR_ACCEL;
      this.vel.x += (wish.x - this.vel.x) * Math.min(1, accel * dt / (this.onGround ? 1 : 4));
      this.vel.z += (wish.z - this.vel.z) * Math.min(1, accel * dt / (this.onGround ? 1 : 4));
      this.vel.y -= GRAV * dt;
      if (k.has("Space") && this.onGround) { this.vel.y = JUMP; this.onGround = false; }
    }

    this.move(dt);
  }

  touchingLadder(margin = 0) {
    return this.nearestLadderCell(margin) !== null;
  }

  // Nearest climbable cell our (margin-expanded) AABB overlaps, or null.
  nearestLadderCell(margin = 0) {
    const g = this.grid;
    const r = HALF + margin;
    const x0 = Math.floor(this.pos.x - r), x1 = Math.floor(this.pos.x + r);
    const z0 = Math.floor(this.pos.z - r), z1 = Math.floor(this.pos.z + r);
    const y0 = Math.floor(this.pos.y), y1 = Math.floor(this.pos.y + HEIGHT - 0.1);
    let best = null, bd = Infinity;
    for (let x = x0; x <= x1; x++)
      for (let y = y0; y <= y1; y++)
        for (let z = z0; z <= z1; z++)
          if (g.climbable(x, y, z)) {
            const d = Math.hypot(x + 0.5 - this.pos.x, z + 0.5 - this.pos.z);
            if (d < bd) { bd = d; best = [x, y, z]; }
          }
    return best;
  }

  collides(px, py, pz, half = HALF) {
    const g = this.grid;
    const x0 = Math.floor(px - half), x1 = Math.floor(px + half);
    const z0 = Math.floor(pz - half), z1 = Math.floor(pz + half);
    const y0 = Math.floor(py + 0.001), y1 = Math.floor(py + HEIGHT - 0.02);
    for (let x = x0; x <= x1; x++)
      for (let y = y0; y <= y1; y++)
        for (let z = z0; z <= z1; z++)
          if (g.solid(x, y, z)) return true;
    return false;
  }

  move(dt) {
    const p = this.pos, v = this.vel;
    // slimmer profile on a ladder so a one-meter deck hatch is passable
    const half = this.onLadder ? 0.2 : HALF;
    // X axis, with step-up assist
    let nx = p.x + v.x * dt;
    if (this.collides(nx, p.y, p.z, half)) {
      if (this.onGround && !this.collides(nx, p.y + 1.05, p.z, half) && Math.abs(v.y) < 0.01) {
        p.y += 1.05; p.x = nx; // hop a deck lip / hatch coaming
      } else v.x = 0;
    } else p.x = nx;
    // Z axis
    let nz = p.z + v.z * dt;
    if (this.collides(p.x, p.y, nz, half)) {
      if (this.onGround && !this.collides(p.x, p.y + 1.05, nz, half) && Math.abs(v.y) < 0.01) {
        p.y += 1.05; p.z = nz;
      } else v.z = 0;
    } else p.z = nz;
    // Y axis
    let ny = p.y + v.y * dt;
    this.onGround = false;
    if (this.collides(p.x, ny, p.z, half)) {
      if (v.y < 0) this.onGround = true;
      v.y = 0;
    } else p.y = ny;

    // Failsafe: fell off the ship into the sea -> respawn on the quarterdeck.
    if (p.y < -30) { p.set(88.5, 14.01, 17.5); v.set(0, 0, 0); this.overboard = true; }
  }
}
